<template>
  <div class="view-wrapper">
    <div class="dialog_content">
      <!-- Cashbook V2 layout (gated by isCashbookV2Enabled, e.g. Fliplab Swiss). -->
      <div
        v-if="isV2"
        class="flex flex-col items-center justify-center gap-8 p-8 text-center"
        data-cy="sales-closed-banner-v2"
      >
        <img
          src="@/assets/images/svg/close-pos.svg"
          alt="sales closed"
          class="w-40 h-auto"
        />
        <div class="flex flex-col gap-2">
          <p class="text-2xl font-bold">
            {{ $t("salesclosedbanner.sales-closed") }}
          </p>
          <p class="text-base text-muted-foreground">
            {{ $t("salesclosedbanner.v2.something-available") }}
          </p>
        </div>
        <div
          v-if="isOnWebPos"
          class="flex items-start gap-3 bg-sky-50/70 border border-sky-200 text-sky-800 rounded-md px-4 py-3 max-w-md text-left"
          data-cy="v2-web-pos-cashbook-warning"
        >
          <InfoIcon class="w-4 h-4 mt-0.5 shrink-0" />
          <div class="text-sm">
            <p class="font-semibold">
              {{ $t("salesclosedbanner.v2.web-cashbook-unavailable-title") }}
            </p>
            <p class="text-sky-700">
              {{ $t("salesclosedbanner.v2.web-cashbook-unavailable-body") }}
            </p>
          </div>
        </div>
        <div
          v-else-if="!isDeviceLinked"
          class="flex items-start gap-3 bg-amber-50/70 border border-amber-300 text-amber-900 rounded-md px-4 py-3 max-w-md text-left"
          data-cy="v2-device-not-linked-warning"
        >
          <TriangleAlertIcon class="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
          <div class="text-sm">
            <span class="font-semibold">
              {{ $t("salesclosedbanner.v2.device-not-linked-title") }}
            </span>
            <span class="text-amber-800">
              {{ $t("salesclosedbanner.v2.device-not-linked-body") }}
            </span>
            <button
              type="button"
              class="underline font-medium text-amber-900 hover:text-amber-950 ml-1"
              data-cy="v2-open-device-settings"
              @click="openDeviceSettings"
            >
              {{ $t("salesclosedbanner.v2.open-device-settings") }} →
            </button>
          </div>
        </div>
        <SButton
          type="button"
          size="lg"
          @click="startSales()"
          data-cy="open-sales-button"
        >
          {{ $t("salesclosedbanner.v2.open-sales") }}
        </SButton>
      </div>

      <!-- Legacy layout (V1) — do not modify; preserves behavior for every non-V2 group. -->
      <div
        v-else
        class="d-flex fl-col fl-s-b f-center"
        style="align-items: center"
      >
        <div class="d-flex fl-col fl-j-c f-center">
          <p style="font-size: 35px; text-align: center">
            <strong>{{ $t("salesclosedbanner.sales-closed") }}</strong>
          </p>
          <p style="font-size: 30px; margin-top: 10px">
            {{ $t("salesclosedbanner.something-available") }}
          </p>
        </div>
        <img
          src="@/assets/images/svg/close-pos.svg"
          alt="sales closed"
          class="closed-image"
        />
        <SButton
          type="button"
          size="lg"
          style="margin: 40px"
          @click="startSales()"
          data-cy="open-sales-button"
        >
          {{ $t("salesclosedbanner.open-sales") }}
        </SButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useI18n } from "vue-i18n";
import { computed, defineComponent } from "vue";
import { openTopUpForm } from "@/utils/vue-helpers/cash-float";
import { isCashbookV2Enabled } from "@/utils/helpers/feature-flags";
import { SButton } from "@smeetz/ui-core";
import { InfoIcon, TriangleAlertIcon } from "@smeetz/ui-icons";
import DeviceModule from "@/store/modules/device";
import ModalModule from "@/store/modules/modal";
import { ModalStatus } from "@/constants/modal";

export default defineComponent({
  name: "SalesClosedBanner",
  components: { SButton, InfoIcon, TriangleAlertIcon },
  setup() {
    const { t } = useI18n({});
    const isV2 = computed(() => isCashbookV2Enabled());
    // Web POS has no Electron bridge, so cross-device cash transfers can't happen.
    const isOnWebPos = computed(() => !window?.electronAPI);
    const isDeviceLinked = computed(() => !!DeviceModule.hardwareLocation);
    const startSales = async () => {
      await openTopUpForm();
    };
    const openDeviceSettings = () => {
      ModalModule.setStatus(ModalStatus.Settings);
    };
    return {
      startSales,
      t,
      isV2,
      isOnWebPos,
      isDeviceLinked,
      openDeviceSettings,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;
.view-wrapper {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  width: 100%;
  justify-content: center;
  .dialog_content {
    margin: auto;
    background: $background-color-base;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    min-width: 500px;
    min-height: 400px;
    width: 100%;
    height: 100%;
  }

  .closed-image {
    width: 39%;
    height: 30%;
  }

  .open-sales-btn {
    padding: 30px 40px;
    font-weight: bold;
    color: $color-text-primary;
    border-radius: 6px;
    border: $color-dark-blue 2px solid;
  }
}
</style>
